import axios from 'axios'
import { CSRF_HEADER, captureCsrfToken, getCsrfToken } from './csrfToken'

/**
 * Attach the Rails CSRF token to state-changing requests on an axios instance.
 *
 * PLT-2324 — the legacy frontend sends no token on any XHR, including login,
 * which makes it the largest tokenless write surface in the product and the
 * blocker on PLT-2105's `CSRF_MODE=enforce` flip.
 *
 * WHY THIS IS A FUNCTION TAKING AN INSTANCE, AND NOT ONE REGISTRATION. There is
 * no single axios object in this app. `AxiosService.js` builds one with
 * `axios.create()`, `api/index.js` builds another per `getApi()` call, and ~31
 * modules import the bare default export. **An axios instance inherits config
 * from the default but NOT its interceptors**, so registering once anywhere
 * covers exactly one of them. Each instance that issues cookie-authenticated
 * writes has to opt in — which is also the trap for the next one: an
 * `axios.create()` added later reaches Rails with no token and nothing fails
 * loudly, it just goes out unverified.
 *
 * TWO GATES, and the origin one is the control that matters. A CSRF token is
 * session-scoped and useless elsewhere, but handing one to a third-party host is
 * still a leak, so a request is only stamped when its resolved origin is the
 * page's own. The method gate keeps it off reads, which Rails never verifies.
 * Both fail closed: an unparseable URL is treated as external.
 *
 * The token is read per request rather than captured once, because Devise
 * rotates the session — and with it the token — on sign-in. With no
 * `csrf_meta_tags` on the page the read returns null, nothing is attached, and
 * the request behaves exactly as it does today.
 */
const WRITE_METHODS = ['post', 'put', 'patch', 'delete']

const isSameOriginRequest = (url, baseURL) => {
  try {
    // A relative URL resolves against `baseURL` when the instance sets one, and
    // otherwise against the page — which is the Rails origin on these pages.
    const base = baseURL
      ? new URL(baseURL, window.location.href).toString()
      : window.location.href

    return new URL(url ?? '', base).origin === window.location.origin
  } catch (error) {
    return false
  }
}

// Per-instance, so a second call cannot stack a second interceptor and set the
// header twice. A WeakSet keeps no strong reference to a discarded instance.
const installedOn = new WeakSet()

export const installCsrfInterceptor = (instance = axios) => {
  if (installedOn.has(instance)) {
    return instance
  }
  installedOn.add(instance)

  instance.interceptors.request.use(
    (config) => {
      if (!WRITE_METHODS.includes((config.method || '').toLowerCase())) {
        return config
      }

      if (!isSameOriginRequest(config.url, config.baseURL)) {
        return config
      }

      const token = getCsrfToken()
      if (token) {
        config.headers = { ...config.headers, [CSRF_HEADER]: token }
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  // Capture, so the tab heals after `rotate_session` swaps the raw token out from
  // under the mask the page was rendered with. See `csrfToken.js` for why the meta
  // tag alone cannot survive that.
  //
  // ON EVERY RESPONSE, not just writes: a GET is usually what a poisoned tab issues
  // next, and healing on it means the following write already carries the new token.
  //
  // BOTH ARMS. The failing-write case IS the error arm — a tab whose recent traffic
  // is all 4xx is precisely the tab that needs to heal, so a success-only capture
  // fixes every tab except the broken one.
  //
  // Gated on origin like the attach, in the opposite direction: there it stops our
  // token leaking to a third party, here it stops a third-party response writing
  // OUR holder. Both fail closed on an unparseable URL.
  const capture = (config, headers) => {
    if (config && isSameOriginRequest(config.url, config.baseURL)) {
      captureCsrfToken(headers)
    }
  }

  instance.interceptors.response.use(
    (response) => {
      capture(response?.config, response?.headers)
      return response
    },
    (error) => {
      // `error.response` is absent on a network failure or a cancellation;
      // `error.config` still names the request, and there are no headers to read.
      capture(error?.config ?? error?.response?.config, error?.response?.headers)
      return Promise.reject(error)
    }
  )

  return instance
}

export default installCsrfInterceptor
